import { isEqual } from 'lodash';
import { createContext, useContext, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { CUSTOM_VALUE_SET_NAME } from '../../../../../../../common/model/input/value-sets/value-sets';
import { setValueSet } from '../../../../../../untidy/redux/actions/actions';
import { getValueSet, getValueSetName } from '../../../../../../untidy/redux/selectors';
import { usePrevious } from '../../../../../generic/hooks';
import { getValueSetFromSelection } from './get-value-set-from-selection';
import { initializeSelectionStates } from './initialize-selection-states';
import { StateHooksOfValueSetSelector, ValueSetWithName } from './types';
import { updateSelectionToMatchValueSet } from './update-selection-to-match-value-set';

interface ValuesSelectorContext {
  stateHooks: StateHooksOfValueSetSelector;
  selectedValueSet: ValueSetWithName | undefined;
  setSelectedValueSet: (vs: ValueSetWithName) => void;
}

export function useValuesSelectorContextBuilder(): ValuesSelectorContext {
  const [selectedValueSet, setSelectedValueSet] = useState<ValueSetWithName | undefined>(undefined);
  const stateHooks: StateHooksOfValueSetSelector = initializeSelectionStates(setSelectedValueSet);

  const valueSet = useSelector(getValueSet);
  const valueSetName = useSelector(getValueSetName);

  const dispatch = useDispatch();

  useEffect(() => {
    if (valueSetName === CUSTOM_VALUE_SET_NAME) {
      return;
    }
    setSelectedValueSet({ valueSet, name: valueSetName });
  }, [valueSet, valueSetName]);

  const updatedValueSet = getValueSetFromSelection(stateHooks);
  const previousValueSet = usePrevious(updatedValueSet);
  const valueSetChanged = isEqual(updatedValueSet, previousValueSet);

  useEffect(() => {
    if (selectedValueSet?.name !== CUSTOM_VALUE_SET_NAME || !valueSetChanged) {
      return;
    }
    updateValueSetToRedux({ valueSet: updatedValueSet, name: CUSTOM_VALUE_SET_NAME });
  }, [valueSetChanged]);

  function updateValueSetAndToggles(vs: ValueSetWithName) {
    setSelectedValueSet(vs);
    updateSelectionToMatchValueSet(stateHooks, vs);
    updateValueSetToRedux(vs);
  }

  function updateValueSetToRedux(vs: ValueSetWithName) {
    dispatch(setValueSet(vs.valueSet, vs.name));
  }

  return {
    stateHooks,
    selectedValueSet,
    setSelectedValueSet: vs => updateValueSetAndToggles(vs),
  };
}

const ctx = createContext({} as ValuesSelectorContext);

export function useValuesSelectorContext() {
  return useContext(ctx);
}

export const ValuesSelectorContextProvider = ctx.Provider;
